import type { ButtonHTMLAttributes, ReactNode } from "react";

type ChipVariant =
	| "primary"
	| "success"
	| "warning"
	| "danger"
	| "info"
	| "slate";

type ChipSize = "xs" | "sm" | "md";

interface ChipProps {
	variant?: ChipVariant;
	size?: ChipSize;
	children: ReactNode;
	className?: string;
}

interface ChipButtonProps
	extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "className"> {
	variant?: ChipVariant;
	size?: ChipSize;
	children: ReactNode;
	className?: string;
}

const variantStyles: Record<ChipVariant, string> = {
	primary: "border-primary/30 bg-primary/5 text-primary",
	success: "border-success/40 bg-success/5 text-success",
	warning: "border-warning-dark/30 bg-warning/5 text-warning-dark",
	danger: "border-danger/30 bg-danger/5 text-danger",
	info: "border-info/30 bg-info/5 text-info",
	slate: "border-slate-300 bg-slate-50 text-slate-600",
};

const variantHoverStyles: Record<ChipVariant, string> = {
	primary: "hover:bg-primary/15",
	success: "hover:bg-success/15",
	warning: "hover:bg-warning/15",
	danger: "hover:bg-danger/15",
	info: "hover:bg-info/15",
	slate: "hover:bg-slate-100",
};

const sizeStyles: Record<ChipSize, string> = {
	xs: "px-2 py-0.5 text-[11px]",
	sm: "px-2.5 py-0.5 text-xs",
	md: "px-2.5 py-1 text-xs",
};

/**
 * Outlined pill chip for labels, tags, and badges.
 */
export const Chip = ({
	variant = "slate",
	size = "sm",
	children,
	className = "",
}: ChipProps) => (
	<span
		className={`inline-flex items-center rounded-full border font-semibold ${variantStyles[variant]} ${sizeStyles[size]} ${className}`}
	>
		{children}
	</span>
);

/**
 * Clickable chip button — same outlined pill style with hover effects.
 */
export const ChipButton = ({
	variant = "slate",
	size = "md",
	children,
	className = "",
	...rest
}: ChipButtonProps) => (
	<button
		type="button"
		className={`inline-flex items-center rounded-full border font-semibold transition-colors hover:cursor-pointer ${variantStyles[variant]} ${variantHoverStyles[variant]} ${sizeStyles[size]} ${className}`}
		{...rest}
	>
		{children}
	</button>
);
